// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// If you have dependencies that try to import CSS, esbuild will generate a separate `app.css` file.
// To load it, simply add a second `<link>` to your `root.html.heex` file.

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html"
// Establish Phoenix Socket and LiveView configuration.
import {Socket} from "phoenix"
import {LiveSocket} from "phoenix_live_view"
import {KeepAwake} from "./keep_awake"
import {hooks as colocatedHooks} from "phoenix-colocated/andi"
// 2026-08-31 (Max): resolvable since catalogue moved to the mdon fork at
// PR #90 — its mix.exs now lists the :phoenix_live_view compiler, so the
// manifest the A079 note found missing gets written. Carries the real
// ItemPicker keyboard hook plus the new QtySignal / ScrollTop / AutoLoad.
import {hooks as catalogueHooks} from "phoenix-colocated/phoenix_kit_catalogue"
import topbar from "../vendor/topbar"
// Leaf rich-text editor (used by the phoenix_kit_comments composer). Standalone
// vanilla JS that registers its hook on window.LeafHooks; required so the
// editor's phx-hook="Leaf" initializes instead of hanging on its loading text.
import "../../deps/leaf/priv/static/assets/leaf.js"

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")

// This browser tab's id for the order edit lock (reconnects of the same tab keep
// it, another tab or device gets another one). Made once, at module load, and
// never allowed to break the LiveView connection: no crypto.randomUUID ->
// 16 random bytes in the UUID layout (lowercase, the server accepts [0-9a-f-]{36}),
// no sessionStorage -> a fresh id per page load.
function makeClientId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID().toLowerCase()
    }
    const b = window.crypto.getRandomValues(new Uint8Array(16))
    b[6] = (b[6] & 0x0f) | 0x40
    b[8] = (b[8] & 0x3f) | 0x80
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("")
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
  } catch (_e) {
    return null
  }
}

const clientId = (() => {
  let stored = null
  try {
    stored = window.sessionStorage.getItem("andi_client_id")
  } catch (_e) {}
  if (stored && /^[0-9a-f-]{36}$/.test(stored)) return stored
  const fresh = makeClientId()
  if (fresh) {
    try {
      window.sessionStorage.setItem("andi_client_id", fresh)
    } catch (_e) {}
  }
  return fresh
})()

// Fresco canvas + Etcher annotation layer. Importing each runs its IIFE which
// registers window.FrescoHooks / window.EtcherHooks. Required so the annotation
// canvas (drawing) and the AnnotationFlattener offscreen render initialize.
import "../../deps/fresco/priv/static/fresco.js"
import "../../deps/etcher/priv/static/etcher.js"
// Tessera zoom layer (progressive medium → large → original swap in the media
// lightbox). PhoenixKit's own TesseraLayer stub lazy-loads it from jsDelivr,
// which our CSP (script-src 'self') blocks — so import it locally like Fresco
// and Etcher above; the stub sees window.TesseraHooks.TesseraLayer and skips
// the CDN. Without this the lightbox stays on the 300px `small` variant.
import "../../deps/tessera/priv/static/tessera.js"

// Self-host SortableJS so the PhoenixKit SortableGrid hook uses window.Sortable
// (its `if (window.Sortable)` early return) instead of its CDN fallback, which
// the app's Content-Security-Policy blocks. Vendored (not npm) to match the
// assets/vendor convention and stay deploy-safe. Must run before the hooks below.
import Sortable from "../vendor/sortable.complete.esm.js"
window.Sortable = Sortable

// Load PhoenixKit hooks (RowMenu, SortableGrid, etc.)
import "../../deps/phoenix_kit/priv/static/assets/phoenix_kit.js"

// PhoenixKitCatalogue (0.23.0 as of this note, was 0.8.0 when it was first
// written) ships ItemPicker's keyboard navigation as a LiveView *colocated*
// hook, not a prebuilt priv/static bundle — that part of the old note still
// holds. PickerInputShim below is unrelated: it only works around the dep's
// phx-change/name bug on the search input, it does not stand in for the
// colocated keyboard hook. See CatalogueItemPickerKeyboardNav further down
// (next to `allHooks`) for why that hook can't simply be `import`ed here and
// how it's wired instead.

// Patch TableCardView to re-apply saved view mode after LiveView updates
if (window.PhoenixKitHooks && window.PhoenixKitHooks.TableCardView) {
  const original = window.PhoenixKitHooks.TableCardView
  window.PhoenixKitHooks.TableCardView = {
    ...original,
    updated() {
      var saved = localStorage.getItem(this.storageKey) || "table"
      this.applyMode(saved)
    }
  }
}

// ANDI: Shim around phoenix_kit_catalogue's <.item_picker>. The dep's inner
// combobox input has phx-change="query_change" but no `name` attribute and is
// not wrapped in a <form> — LiveView 1.x then either rejects the event
// outright (view.js:1703) or serialises an empty payload, which crashes the
// dep's handler with FunctionClauseError and bounces the parent LV (the row
// appears to "delete itself"). Even injecting name="value" via JS doesn't
// stick because morphdom strips it on every re-render of the live_component.
//
// Workaround: strip phx-change from the input on mount and on every update,
// listen to `input` events ourselves with a 300ms debounce, and push
// query_change directly to the picker live_component via pushEventTo. Also
// select the existing text on focus so typing replaces the pre-filled item
// name instead of appending to it.
const PickerInputShim = {
  mounted() {
    this._wireInput()
    // Row pickers (opened on demand via "подобрать вручную") carry
    // data-autofocus: drop the cursor straight into the search input — which
    // is pre-filled with the row name — so the user can refine immediately.
    // The category pickers (always rendered) omit the flag, so they don't
    // steal focus on page load. Focusing fires the existing focus listener,
    // which selects the pre-filled text for type-to-replace.
    if (this.el.hasAttribute("data-autofocus")) {
      const input = this.el.querySelector('input[role="combobox"]')
      if (input) input.focus()
    }
  },
  updated() {
    this._wireInput()
  },
  _wireInput() {
    const input = this.el.querySelector('input[role="combobox"]')
    if (!input) return
    // Always strip — morphdom re-adds it from the dep's template on every
    // patch, so this must run on both mount and updated.
    input.removeAttribute("phx-change")
    // Wired-flag stored as a JS property (not dataset) so morphdom can't wipe
    // it. Without this we'd attach a fresh listener on every re-render and
    // leak handlers.
    if (input.__andiPickerShim) return
    input.__andiPickerShim = true

    // Derive the live_component target from the input's own id (the dep
    // always renders it as "<component-id>-input") rather than from the
    // wrapper form id. The old form-id surgery (replace /wrap$/) silently
    // broke whenever the form id wasn't exactly "<component-id>wrap" — e.g.
    // a "<component-id>-wrap" id left a trailing dash and pushed to a
    // non-existent target, so typing never searched.
    const targetId = "#" + input.id.replace(/-input$/, "")
    let timer = null

    // Capture-phase listeners stop the event before LV's document-level
    // delegation sees it. Otherwise, even with phx-change stripped above,
    // there's a race window after morphdom re-adds the attribute (between
    // the patch and our updated() callback) where LV would fire its broken
    // form-serialised event and crash the live_component.
    const block = (e) => {
      e.stopImmediatePropagation()
    }
    input.addEventListener("change", block, { capture: true })
    input.addEventListener("input", (e) => {
      e.stopImmediatePropagation()
      clearTimeout(timer)
      const value = e.target.value
      timer = setTimeout(() => {
        this.pushEventTo(targetId, "query_change", { value })
      }, 300)
    }, { capture: true })

    input.addEventListener("focus", () => {
      requestAnimationFrame(() => input.select())
    })
  }
}

// Inventory count sheet: Enter in a count/price/sum/note cell should COMMIT the
// typed value and deactivate the field — never submit the form. These inputs sit
// in a `phx-change` form without `phx-submit`; on Enter LiveView's external-submit
// fallback (live_socket: form.submit()) would reload the page, remount the
// LiveView and re-read the doc from the DB — discarding the typed count and any
// freshly-added (unsaved) lines. We intercept Enter, prevent that submit, and
// blur the input so the existing `phx-debounce="blur"` change commits the value.
// (The forms also carry `phx-submit` as a no-JS fallback that commits without a
// reload; with this hook active that path is never reached.)
const InvEnterBlur = {
  mounted() {
    this._onKey = (e) => {
      if (e.key === "Enter") {
        e.preventDefault()
        this.el.blur()
      }
    }
    this.el.addEventListener("keydown", this._onKey)
  },
  destroyed() {
    if (this._onKey) this.el.removeEventListener("keydown", this._onKey)
  }
}

// Per-viewer toggle for category/catalogue preview thumbnails.
// The tree renders BOTH the thumbnail and the original folder icon wherever a
// featured image exists (icon starts hidden); this hook swaps which of the two
// is visible and remembers the choice in localStorage, per browser. It re-applies
// on `updated()` because LiveView re-renders the tree on every expand/collapse
// and morphdom would otherwise restore the server-rendered default. Same shape
// as the TableCardView patch above, which solves this exact problem.
// Rendered as a 3-way segmented control (one <button data-mode="..."> per
// mode) instead of a single cycling button, so the active mode is visible
// without clicking. Storage/body-class mechanism is unchanged from the old
// cycling version — only the click wiring (direct mode instead of "next in
// list") and the active-button highlight are new.
const CategoryThumbToggle = {
  storageKey: "andi:catalogue:thumbs",
  // Значение хранится per-viewer; прежние двухпозиционные значения ("on")
  // не входят в список и мягко читаются как "normal", поэтому старый выбор
  // никого не ломает.
  modes: ["normal", "big", "off"],
  mounted() {
    this.apply(this.read(), false)
    this.el.querySelectorAll("[data-mode]").forEach((btn) => {
      btn.addEventListener("click", () => this.apply(btn.dataset.mode, true))
    })
  },
  updated() {
    this.highlight(this.read())
  },
  read() {
    try {
      const v = localStorage.getItem(this.storageKey)
      return this.modes.indexOf(v) >= 0 ? v : "normal"
    } catch (e) {
      return "normal"
    }
  },
  apply(mode, persist) {
    if (persist) {
      try {
        localStorage.setItem(this.storageKey, mode)
      } catch (e) {
        // приватный режим: переключатель всё равно работает в этой вкладке
      }
    }
    document.body.classList.toggle("cat-thumbs-off", mode === "off")
    document.body.classList.toggle("cat-thumbs-big", mode === "big")
    this.highlight(mode)
  },
  // A073 rework, defect 2: the three buttons no longer live inside a `join`
  // group (owner: they must read as separate buttons, not one fused
  // section), so the active one can no longer lean on `join`'s shared
  // border for a segmented look. Marking it with a fill (`btn-active`)
  // instead was rejected too — the owner asked specifically for a border/
  // frame ("рамкой играть"), not a fill-color change. All three buttons
  // stay `btn-outline` (transparent background) always; only the active
  // one gets an extra `outline` ring around it.
  highlight(mode) {
    this.el.querySelectorAll("[data-mode]").forEach((btn) => {
      const active = btn.dataset.mode === mode
      btn.classList.toggle("outline", active)
      btn.classList.toggle("outline-2", active)
      btn.classList.toggle("outline-primary", active)
      btn.classList.toggle("outline-offset-2", active)
      btn.classList.toggle("text-base-content/70", !active)
    })
  }
}

// Collapses/expands the preliminary-import block. Same body-class
// mechanism as CategoryThumbToggle above — the block lives inside the
// re-rendering catalogue tree, so state on the block itself would be lost
// to morphdom; a class on <body> survives every re-render.
const ImportBlockToggle = {
  storageKey: "andi:catalogue:import-open",
  mounted() {
    this.apply(this.read(), false)
    this.el.addEventListener("click", () => {
      this.apply(!this.read(), true)
    })
  },
  read() {
    try {
      return localStorage.getItem(this.storageKey) === "1"
    } catch (e) {
      return false
    }
  },
  apply(open, persist) {
    if (persist) {
      try {
        localStorage.setItem(this.storageKey, open ? "1" : "0")
      } catch (e) {
        // приватный режим: переключатель всё равно работает в этой вкладке
      }
    }
    document.body.classList.toggle("import-open", open)
  }
}

// A079: PhoenixKitCatalogue/Locations/Manufacturing each define a hook
// (`.ItemPicker`, `.PkLocationsUploadScope`, `.PkManufacturingUploadScope`)
// as a LiveView *colocated* hook (Phoenix.LiveView.ColocatedHook). The
// documented way to load one is `import {hooks} from "phoenix-colocated/<app>"`
// — but that import only resolves once LiveView has written a manifest at
// `_build/<env>/phoenix-colocated/<app>/index.js`, and it only ever writes
// that manifest for an app whose OWN mix.exs lists `:phoenix_live_view` in
// `compilers:` (see `Phoenix.LiveView.ColocatedAssets.compile/0` — the write
// happens from an `after_compiler(:elixir, ...)` hook that custom compiler
// registers for whichever project is compiling). None of these three deps'
// mix.exs do (`grep compilers deps/phoenix_kit_{catalogue,locations,manufacturing}/mix.exs`
// is empty for all three) — only `andi`'s own mix.exs does, which is why
// `_build/dev/phoenix-colocated/andi/index.js` exists but the three deps'
// folders only ever accumulate raw per-hook .js files, never an index.js.
// Confirmed empirically: bundling a throwaway file with
// `import {hooks} from "phoenix-colocated/phoenix_kit_catalogue"` (same
// esbuild + NODE_PATH as config/config.exs) fails with "Could not resolve" —
// adding that import here, as the original diagnosis assumed, would not
// silence the console errors, it would break the whole app.js build.
//
// Until the three deps register that compiler upstream, the three hooks are
// reimplemented here verbatim, ported from their colocated source under
// `_build/dev/phoenix-colocated/<dep>/<Module>/*.js`, registered under the
// exact dotted name LiveView renders for a colocated hook (`<Module>.<Name>`)
// so `phx-hook="...ItemPicker"` etc. resolve against `allHooks` below.
const CatalogueItemPickerKeyboardNav = {
  mounted() {
    this.input = this.el.querySelector('input[role="combobox"]')
    this.focusedIdx = -1
    this._optSig = ""
    this._onKey = (e) => this.handleKey(e)
    this.input.addEventListener("keydown", this._onKey)
  },
  updated() {
    // A NEW option set (debounced search landed) resets the highlight —
    // carrying the old index over highlights whatever now sits at that
    // position, and Enter would activate an item the user never focused.
    // Same-set re-renders only clamp. Signature is by uuid: option ids are
    // positional and identical across different result sets of the same
    // length.
    const opts = this.enabledOptions()
    const sig = opts.map((o) => o.getAttribute("phx-value-uuid")).join(" ")
    if (sig !== this._optSig) {
      this._optSig = sig
      this.focusedIdx = -1
    } else if (this.focusedIdx >= opts.length) {
      this.focusedIdx = opts.length - 1
    }
    this.syncActiveDescendant()
  },
  destroyed() {
    if (this.input && this._onKey) {
      this.input.removeEventListener("keydown", this._onKey)
    }
  },
  enabledOptions() {
    return Array.from(
      this.el.querySelectorAll('li[role="option"]:not([aria-disabled="true"])')
    )
  },
  handleKey(e) {
    const opts = this.enabledOptions()
    switch (e.key) {
      case "ArrowDown":
        if (opts.length === 0) return
        e.preventDefault()
        this.focusedIdx = Math.min(this.focusedIdx + 1, opts.length - 1)
        if (this.focusedIdx < 0) this.focusedIdx = 0
        this.syncActiveDescendant()
        break
      case "ArrowUp":
        if (opts.length === 0) return
        e.preventDefault()
        this.focusedIdx = Math.max(this.focusedIdx - 1, 0)
        this.syncActiveDescendant()
        break
      case "Home":
        if (opts.length === 0) return
        e.preventDefault()
        this.focusedIdx = 0
        this.syncActiveDescendant()
        break
      case "End":
        if (opts.length === 0) return
        e.preventDefault()
        this.focusedIdx = opts.length - 1
        this.syncActiveDescendant()
        break
      case "Enter":
        if (this.focusedIdx >= 0 && this.focusedIdx < opts.length) {
          e.preventDefault()
          opts[this.focusedIdx].click()
        }
        break
      case "Escape":
        e.preventDefault()
        this.pushEventTo(this.el, "close", {})
        break
    }
  },
  syncActiveDescendant() {
    this.el
      .querySelectorAll('li[data-focused="true"]')
      .forEach((el) => el.removeAttribute("data-focused"))
    const opts = this.enabledOptions()
    if (this.focusedIdx >= 0 && this.focusedIdx < opts.length) {
      const el = opts[this.focusedIdx]
      el.setAttribute("data-focused", "true")
      el.scrollIntoView({block: "nearest"})
      this.input.setAttribute("aria-activedescendant", el.id)
    } else {
      this.input.removeAttribute("aria-activedescendant")
    }
  }
}

// Same tiny hook, verbatim and identical in both deps' colocated source:
// pushes the active upload scope on drag-enter so a drag-and-drop upload
// routes to the right folder even when the user hasn't clicked the dropzone
// first (by the time `drop` fires, the server already has the scope).
const FilesCardUploadScopeOnDragEnter = {
  mounted() {
    const push = () => {
      const scope = this.el.dataset.scope
      if (scope) this.pushEvent("set_active_upload_scope", {scope: scope})
    }
    this.el.addEventListener("dragenter", push)
  }
}

// Merge all hooks: colocated (app) + PhoenixKit + PhoenixKitCatalogue
const allHooks = {
  ...colocatedHooks,
  ...(window.PhoenixKitHooks || {}),
  ...(window.PhoenixKitCatalogueHooks || {}),
  ...(window.FrescoHooks || {}),
  ...(window.EtcherHooks || {}),
  ...(window.TesseraHooks || {}),
  ...(window.LeafHooks || {}),
  PickerInputShim,
  KeepAwake,
  InvEnterBlur,
  CategoryThumbToggle,
  ImportBlockToggle,
  "PhoenixKitCatalogue.Web.Components.ItemPicker.ItemPicker": CatalogueItemPickerKeyboardNav,
  "PhoenixKitLocations.Web.Components.FilesCard.PkLocationsUploadScope": FilesCardUploadScopeOnDragEnter,
  "PhoenixKitManufacturing.Web.Components.FilesCard.PkManufacturingUploadScope": FilesCardUploadScopeOnDragEnter,
  // Last on purpose: the dep's own colocated hooks supersede the A079
  // ports above for catalogue (locations/manufacturing still need theirs —
  // those two deps have not registered the compiler yet).
  ...catalogueHooks
}

// Proxy for hooks registered later (e.g. document_creator inline scripts)
const hooksProxy = new Proxy(allHooks, {
  get(target, prop) {
    if (typeof prop === "symbol") return target[prop]
    if (window.PhoenixKitHooks && window.PhoenixKitHooks[prop]) {
      return window.PhoenixKitHooks[prop]
    }
    if (window.PhoenixKitCatalogueHooks && window.PhoenixKitCatalogueHooks[prop]) {
      return window.PhoenixKitCatalogueHooks[prop]
    }
    return target[prop]
  },
  has(target, prop) {
    if (prop in target) return true
    if (window.PhoenixKitHooks && prop in window.PhoenixKitHooks) return true
    if (window.PhoenixKitCatalogueHooks && prop in window.PhoenixKitCatalogueHooks) return true
    return false
  }
})

// No longPollFallbackMs: on a slow link (owner's iPad, 2026-09-23) the WebSocket
// missed the 2.5 s health-check ping, the client dropped it mid-join for LongPoll,
// the LongPoll poll then failed and the page went to phx-error. WebSocket only.
const liveSocket = new LiveSocket("/live", Socket, {
  params: () => ({_csrf_token: csrfToken, viewport_width: window.innerWidth, client_id: clientId}),
  hooks: hooksProxy,
})

// Open a URL in a new tab after document creation
window.addEventListener("phx:open-google-doc", ({detail}) => {
  if (detail && detail.url) window.open(detail.url, "_blank")
})

// Inventory: after adding a position, focus its "Counted" input for immediate entry.
// Deferred so the focus lands after the add-item modal's dialog.close() restores focus.
window.addEventListener("phx:inv-focus-counted", ({detail}) => {
  if (!detail || !detail.id) return
  setTimeout(() => {
    const el = document.getElementById(detail.id)
    if (el) {
      el.focus()
      try { el.select() } catch (_) {}
    }
  }, 0)
})

// Show progress bar on live navigation and form submits
topbar.config({barColors: {0: "#29d"}, shadowColor: "rgba(0, 0, 0, .3)"})
window.addEventListener("phx:page-loading-start", _info => topbar.show(300))
window.addEventListener("phx:page-loading-stop", _info => topbar.hide())

// connect if there are any LiveViews on the page
liveSocket.connect()

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket

// ANDI: Override document editor "back" button with return-to-order URL
;(function() {
  const returnUrl = sessionStorage.getItem("andi_order_return_url")
  if (!returnUrl) return
  if (!window.location.pathname.includes("/document-creator/documents/")) return

  // Clear so it doesn't persist beyond one use
  sessionStorage.removeItem("andi_order_return_url")

  // The back button is an <a> with hero-arrow-left inside the editor header
  const observer = new MutationObserver((_mutations, obs) => {
    const backLink = document.querySelector('a[href*="/admin/document-creator"] span.hero-arrow-left')?.closest('a')
    if (backLink) {
      backLink.href = returnUrl
      obs.disconnect()
    }
  })
  observer.observe(document.body, {childList: true, subtree: true})

  // Also try immediately in case DOM is already ready
  requestAnimationFrame(() => {
    const backLink = document.querySelector('a[href*="/admin/document-creator"] span.hero-arrow-left')?.closest('a')
    if (backLink) {
      backLink.href = returnUrl
      observer.disconnect()
    }
  })
})()

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === "development") {
  window.addEventListener("phx:live_reload:attached", ({detail: reloader}) => {
    // Enable server log streaming to client.
    // Disable with reloader.disableServerLogs()
    reloader.enableServerLogs()

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location
    let keyDown
    window.addEventListener("keydown", e => keyDown = e.key)
    window.addEventListener("keyup", _e => keyDown = null)
    window.addEventListener("click", e => {
      if(keyDown === "c"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtCaller(e.target)
      } else if(keyDown === "d"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtDef(e.target)
      }
    }, true)

    window.liveReloader = reloader
  })
}

